// The column holds TWO vocabularies: WF24 writes bare ACCESSUnalignmentReasonCS codes,
// this module writes `op:`-prefixed operational ones. Spec: docs/specs/2026-10-01-withdrawal-reason-capture.md

export const NS = 'op';
export const NOTE_MAX = 280;
export const OTHER = 'other';

export const WITHDRAWAL_REASON_CODES = [
  { code: 'never_engaged', label: 'Never engaged after enrolling', cms: 'loss-of-contact' },
  { code: 'lost_contact', label: 'Lost contact after starting', cms: 'loss-of-contact' },
  { code: 'patient_declined', label: 'Patient chose to stop', cms: 'patient-initiated' },
  { code: 'cost_or_insurance', label: 'Cost or insurance concern', cms: 'patient-initiated' },
  { code: 'device_or_tech_barrier', label: 'Cannot use the devices or app', cms: 'patient-initiated' },
  { code: 'clinically_excluded', label: 'No longer clinically eligible', cms: 'no-longer-clinically-eligible' },
  { code: 'relocated', label: 'Moved out of the service area', cms: 'geographic-relocated' },
  { code: 'deceased', label: 'Patient deceased', cms: null },
  { code: 'duplicate_or_test', label: 'Duplicate or test record', cms: null },
  { code: OTHER, label: 'Other', cms: 'patient-initiated' },
];

// WF24's vocabulary. R1 gates on membership in THIS set, not on emptiness: WF24
// writes the reason on the pending_unalignment PATCH too, so an emptiness test
// would let a half-finished unalignment block capture forever.
export const CMS_UNALIGN_REASONS = [
  { code: 'patient-initiated', label: 'Patient-initiated' },
  { code: 'geographic-relocated', label: 'Geographic relocation' },
  { code: 'loss-of-contact', label: 'Loss of contact' },
  { code: 'no-longer-clinically-eligible', label: 'No longer clinically eligible' },
];

const CODES = new Set(WITHDRAWAL_REASON_CODES.map(r => r.code));
const CMS_CODES = new Set(CMS_UNALIGN_REASONS.map(r => r.code));

export const isWithdrawalCode = (c) => CODES.has(String(c ?? ''));
export const isCmsUnalignReason = (v) => CMS_CODES.has(String(v ?? '').trim());
export const withdrawalReasonLabel = (c) =>
  WITHDRAWAL_REASON_CODES.find(r => r.code === c)?.label ?? null;
export const cmsUnalignLabel = (c) =>
  CMS_UNALIGN_REASONS.find(r => r.code === String(c ?? '').trim())?.label ?? null;

// Applied in the browser for the operator's benefit and AGAIN in WF67, which is the
// only real control. WF67's copy is generated from SANITIZE_BODY so the two cannot drift.
export const SANITIZE_BODY = `
  var v = String(s == null ? '' : s);
  v = v.replace(/[\\u0000-\\u001F\\u007F-\\u009F]+/g, ' ');
  // JS \\s covers neither zero-width nor the bidi overrides, and an RTL override
  // reverses the rendering of everything after it — a misleading clinical annotation
  // that passes every other guard.
  v = v.replace(/[\\u200B-\\u200F\\u202A-\\u202E\\u2066-\\u2069\\uFEFF]+/g, '');
  v = v.replace(/\\s+/g, ' ').trim();
  if (v.length > NOTE_MAX) v = v.slice(0, NOTE_MAX);
  // This column exists to be counted in a spreadsheet, where a leading = + - @ is
  // live formula injection. Escape AFTER the cap, so it never eats a character.
  if (/^[=+\\-@]/.test(v)) v = "'" + v;
  return v;`;

// eslint-disable-next-line no-new-func -- one source for the browser and the n8n node
const _sanitize = new Function('s', 'NOTE_MAX', SANITIZE_BODY);
export const sanitizeNote = (raw) => _sanitize(raw, NOTE_MAX);

/** code → `op:code`; `other` + note → `op:other:<note>`. Returns null when there
 *  is nothing to store, so the caller omits the key entirely (R3: never ''). */
export function encodeWithdrawalReason(code, note) {
  if (!isWithdrawalCode(code)) return null;
  if (code !== OTHER) return `${NS}:${code}`;
  const n = sanitizeNote(note);
  return n ? `${NS}:${OTHER}:${n}` : null;
}

/** Decode any value the column can hold. Three vocabularies live here:
 *  ours (`op:`), WF24's bare CMS codes, and legacy hand-written prose. */
export function decodeWithdrawalReason(stored) {
  const s = String(stored ?? '').trim();
  if (!s) return null;
  const m = /^op:([a-z_]+)(?::([\s\S]*))?$/.exec(s);
  if (m) {
    const [, code, note] = m;
    if (!isWithdrawalCode(code)) return { kind: 'unknown', code: null, label: null, note: s };
    return {
      kind: 'operational',
      code,
      label: withdrawalReasonLabel(code),
      note: code === OTHER ? (note ?? '') : null,
    };
  }
  if (isCmsUnalignReason(s)) return { kind: 'cms', code: s, label: cmsUnalignLabel(s), note: null };
  // Legacy free-form prose written straight into the column. Render verbatim —
  // never as "unknown code", which would hide a real operator note.
  return { kind: 'legacy', code: null, label: null, note: s };
}
